import { MouseEvent, RefObject, useCallback, useEffect, useState } from 'react';

interface UseVideoModalProps {
  triggerRef: RefObject<HTMLButtonElement | null>;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  modalRef: RefObject<HTMLDivElement | null>;
}

export function useVideoModal({
  triggerRef,
  closeButtonRef,
  containerRef,
  modalRef,
}: UseVideoModalProps) {
  const [activeVideoId, setActiveVideoId] = useState<string | number | null>(null);

  const closeVideoModal = useCallback(() => {
    setActiveVideoId(null);
    triggerRef.current?.focus();
  }, [triggerRef]);

  const handleClickOutside = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeVideoModal();
      }
    },
    [containerRef, closeVideoModal]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeVideoModal();
    };

    if (activeVideoId) {
      modalRef.current?.focus();
      document.addEventListener('keydown', handleKeyDown);
      document.body.setAttribute('data-scroll-locked', '1');

      closeButtonRef.current?.focus();
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.removeAttribute('data-scroll-locked');
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.removeAttribute('data-scroll-locked');
    };
  }, [activeVideoId, closeVideoModal, closeButtonRef, modalRef]);

  return {
    activeVideoId,
    setActiveVideoId,
    closeVideoModal,
    handleClickOutside,
  };
}
